/**
 * The import orchestrator — ADR-008 §2's pipeline, wired:
 *   Load → Convert(rig) → Map → Synthesize tips (ADR-010 §12) → Declare →
 *   Convert(clips) → Validate.
 * Sequencing and policy only: zero math, zero engine types (the depcruise
 * rule import-orchestrator-no-engine stands guard). Everything loud: the
 * result carries every report the stages produced.
 * Depends on: @harryhausen/adapter-three, @harryhausen/kernel (injected data).
 * Depended on by: app shell (boot probe now, import UI when ADR-006 lands).
 */
import {
  collectMorphTargetNames,
  convertClip,
  convertRig,
  hasTranslationTrack,
  loadBVH,
  loadFBX,
  loadGLB,
  loadSlAnim,
  synthesizableTipGaps,
  synthesizeTips,
  toThreeClip,
  type ClipImportReport,
  type LoadBvhOptions,
  type LoadedBvh,
  type LoadedFbx,
  type LoadedGlb,
  type LoadedSlAnim,
  type SlAnimDroppedJoint,
  type SlAnimMetadata,
  type RigImportReport,
  type SynthesizedTipEntry,
  type TakeClassification,
} from '@harryhausen/adapter-three';
import {
  mapRig,
  parseRigDescriptor,
  profileCoverage,
  promoteMeasuredFacing,
  retargetClip,
  type ClipData,
  type FacingPromotion,
  type MapRigResult,
  type Ontology,
  type PriorsBundle,
  type RetargetResult,
  type RigDescriptor,
} from '@harryhausen/kernel';

export interface ImportDeps {
  readonly ontology: Ontology;
  readonly priors: PriorsBundle;
}

export interface ImportOptions {
  readonly label: string;
  /** Profile whose coverage the import aims for (default 'minimal'). */
  readonly profile?: string;
  readonly declaredFps?: number;
  readonly selectSkeleton?: string;
}

export interface ImportedClip {
  readonly clip: ClipData;
  readonly report: ClipImportReport;
  /** The Declare decision for this clip, recorded: bone or deliberate none. */
  readonly rootMotionTarget: string | null;
}

export interface ImportResult {
  /** Mapped descriptor, tips synthesized, kernel-gated — retarget-ready when review is clear. */
  readonly rig: RigDescriptor;
  readonly clips: readonly ImportedClip[];
  readonly mapping: MapRigResult;
  readonly rigReport: RigImportReport;
  readonly synthesizedTips: readonly SynthesizedTipEntry[];
  /** Ontology IDs still missing after mapping + synthesis — the review UI's queue. */
  readonly missingAfterSynthesis: readonly string[];
  /**
   * Measured-facing promotion (ADR-004 evidence → convention), when the
   * geometry contradicted the declared facing — loud, provenance-carrying,
   * user-correctable downstream (ADR-010 §11 shape). Null = declaration and
   * evidence agree (or evidence indeterminate).
   */
  readonly facingPromotion: FacingPromotion | null;
}

export async function importGlb(
  bytes: ArrayBuffer,
  deps: ImportDeps,
  options: ImportOptions,
): Promise<ImportResult> {
  const loaded = await loadGLB(bytes);
  return importLoaded(loaded, loaded.clips, deps, options);
}

/**
 * BVH joins the SAME pipeline as a mesh-less source (ADR-003 §12): a bare
 * skeleton + one motion block. Only the Load stage is format-specific; from
 * convertRig on it is identical to GLB/FBX. Convention is undeclared by the
 * format, so the caller may override up-axis/units/facing per corpus (CMU is
 * Y-up but scaled; SL/OpenSim is Z-up) — forwarded to loadBVH.
 */
export async function importBvh(
  source: string | ArrayBuffer,
  deps: ImportDeps,
  options: ImportOptions & LoadBvhOptions,
): Promise<ImportResult> {
  const { metersPerUnit, upAxis, facing, ...importOptions } = options;
  const bvhOptions: LoadBvhOptions = {
    ...(metersPerUnit !== undefined ? { metersPerUnit } : {}),
    ...(upAxis !== undefined ? { upAxis } : {}),
    ...(facing !== undefined ? { facing } : {}),
  };
  const loaded = loadBVH(source, bvhOptions);
  return importLoaded(loaded, [loaded.clip], deps, importOptions);
}

export interface SlAnimImportResult extends ImportResult {
  /** The .anim header's play metadata — recoverable ONLY on this rail (ADR-191). */
  readonly slMetadata: SlAnimMetadata;
  /** Joints named in the file but absent from the canonical skeleton — loud. */
  readonly droppedJoints: readonly SlAnimDroppedJoint[];
}

/**
 * SL/OpenSim binary .anim joins the SAME pipeline as a mesh-less source
 * (ADR-003 §12): the vendored canonical Linden skeleton + one motion block.
 * Only the Load stage is format-specific; convention is fixed by the spec
 * (Z-up, +X facing, meters — no per-corpus override, unlike BVH). The header
 * metadata (priority/loop/ease/hand-pose) rides on the result for the bake to
 * carry into ClipRecord/catalog rows — it does not affect the retarget math.
 */
export async function importSlAnim(
  bytes: ArrayBuffer,
  deps: ImportDeps,
  options: ImportOptions,
): Promise<SlAnimImportResult> {
  const loaded = loadSlAnim(bytes, { label: options.label });
  const result = await importLoaded(loaded, [loaded.clip], deps, options);
  return { ...result, slMetadata: loaded.metadata, droppedJoints: loaded.droppedJoints };
}

/** A take's name and measured classification, surfaced for selection UI. */
export interface FbxTakeSummary {
  readonly name: string;
  readonly classification: TakeClassification;
}

export interface FbxImportResult extends ImportResult {
  /** EVERY take the file carries, classified — including the ones not converted. */
  readonly takes: readonly FbxTakeSummary[];
}

/**
 * FBX joins UPSTREAM of the same conversion pipeline (ADR-008 §2, Load
 * stage): only take enumeration is format-specific. Animation takes are
 * converted; static-pose and empty takes are surfaced by classification —
 * never dropped silently, never auto-played (a Mixamo clips[0] can be the
 * vestigial empty take).
 */
export async function importFbx(
  bytes: ArrayBuffer,
  deps: ImportDeps,
  options: ImportOptions,
): Promise<FbxImportResult> {
  const loaded = loadFBX(bytes);
  const animationClips = loaded.takes.filter((t) => t.classification === 'animation').map((t) => t.clip);
  const result = await importLoaded(loaded, animationClips, deps, options);
  return {
    ...result,
    takes: loaded.takes.map(({ name, classification }) => ({ name, classification })),
  };
}

/** The shared post-Load pipeline — every format funnels through this, unbranched. */
async function importLoaded(
  loaded: LoadedGlb | LoadedFbx | LoadedBvh | LoadedSlAnim,
  threeClips: LoadedGlb['clips'],
  deps: ImportDeps,
  options: ImportOptions,
): Promise<ImportResult> {
  const profile = options.profile ?? 'minimal';

  // ---- Convert(rig): pure extraction, mapping honestly empty.
  const { rig: bareRig, report: rigReport, trackNormalization } = convertRig(loaded.scene, {
    label: options.label,
    ontologyVersion: deps.ontology.version,
    convention: loaded.convention,
    authoredNameFor: loaded.authoredNameFor,
    generator: loaded.generator,
    unhandledExtensions: loaded.unhandledExtensions,
    ...(options.selectSkeleton !== undefined ? { selectSkeleton: options.selectSkeleton } : {}),
  });

  // ---- Map (ADR-004): scored table, gaps enumerated.
  const mapping = mapRig(bareRig, deps.ontology, deps.priors, { profile });

  // ---- Synthesize tips (ADR-010 §12): only tip gaps with mapped parents;
  //      everything else stays in the review queue.
  const gaps = synthesizableTipGaps(mapping, deps.ontology);
  const synth = synthesizeTips(loaded.scene, bareRig, gaps);

  // ---- Merge + Validate: the kernel parser is the gate.
  const merged = parseRigDescriptor({
    ...synth.rig,
    mapping: [...mapping.mapping, ...synth.mappingAdditions],
  });

  // ---- Promote measured facing (ADR-004 evidence → the convention every
  //      downstream consumer reads): a rig that lies about facing gets the
  //      MEASURED one, with provenance, and a reflection re-scan under the
  //      corrected convention. Loud in the result, never silent.
  const { rig, promotion: facingPromotion } = promoteMeasuredFacing(merged, mapping.report);
  const missingAfterSynthesis = profileCoverage(rig, deps.ontology, profile).missing;

  // ---- Declare + Convert(clips): root motion is declared on the Hips-mapped
  //      bone IFF the clip actually carries its translation channel — a
  //      deliberate none otherwise, never a guess (ADR-003 §2c).
  const hipsBone = mapping.mapping.find((m) => m.ontologyId === 'Hips')?.rigBone ?? null;
  const boneNames = new Set(rig.bones.map((b) => b.name));
  const morphTargets = collectMorphTargetNames(loaded.scene);
  const clips: ImportedClip[] = threeClips.map((threeClip) => {
    const rootMotionTarget = hipsBone !== null && hasTranslationTrack(threeClip, hipsBone) ? hipsBone : null;
    const { clip, report } = convertClip(threeClip, boneNames, {
      rootMotionTarget,
      morphTargets,
      trackNormalization,
      ...(options.declaredFps !== undefined ? { declaredFps: options.declaredFps } : {}),
    });
    return { clip, report, rootMotionTarget };
  });

  return { rig, clips, mapping, rigReport, synthesizedTips: synth.synthesized, missingAfterSynthesis, facingPromotion };
}

/**
 * The five-minute-wow core, orchestrated: a clip from one import applied to
 * another import's rig, returned as a playable Three clip via the adapter.
 */
export function retargetImported(
  source: ImportResult,
  clipIndex: number,
  target: ImportResult,
  deps: ImportDeps,
  profile = 'minimal',
): { result: RetargetResult; playable: ReturnType<typeof toThreeClip> } {
  const imported = source.clips[clipIndex];
  if (!imported) throw new RangeError(`source "${source.rig.label}" has no clip ${clipIndex}`);
  const result = retargetClip(
    deps.ontology,
    { rig: source.rig, clip: imported.clip },
    { rig: target.rig },
    { profile },
  );
  return { result, playable: toThreeClip(result.clip) };
}
